import { cx, focusRing } from "components/Graphs/utils";
import { ArrowUpRight } from "lucide-react";
import React from "react";

import BalanceStatusIndicator, {
  type BalanceStatusTone,
} from "../BalanceStatusIndicator";

export interface CurrentBalanceProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "onClick"
> {
  amount: string;
  label?: string;
  isLoading?: boolean;
  statusTone?: BalanceStatusTone;
  isStatusLoading?: boolean;
  valueTone?: "default" | "danger" | "cta";
  onActivate?: () => void;
  className?: string;
}

export const CurrentBalance = React.forwardRef<
  HTMLElement,
  CurrentBalanceProps
>(function CurrentBalance(
  {
    amount,
    label = "Current balance",
    isLoading = false,
    statusTone,
    isStatusLoading = false,
    valueTone = "default",
    onActivate,
    className,
    ...triggerProps
  },
  forwardedRef,
) {
  const content = (
    <>
      <BalanceStatusIndicator tone={statusTone} isLoading={isStatusLoading} />
      <span
        className={cx(
          "text-[10px] uppercase tracking-wide text-zinc-400",
          label === "Current balance" ? "hidden lg:inline" : "inline",
        )}
      >
        {label}
      </span>
      <span
        className={cx(
          "font-mono text-xs tabular-nums text-zinc-200",
          valueTone === "danger" && "text-red-400",
          valueTone === "cta" && "text-highlight",
          isLoading &&
            "h-3 w-12 animate-pulse rounded-sm bg-zinc-700 text-transparent",
        )}
      >
        {isLoading ? "Loading" : amount}
      </span>
      {!isLoading && valueTone === "cta" ? (
        <ArrowUpRight className="size-3 text-highlight" aria-hidden="true" />
      ) : null}
    </>
  );

  const sharedClassName = cx(
    "flex h-9 shrink-0 items-center gap-2 rounded-sm border border-zinc-800 bg-black/25 px-2.5 shadow-[inset_0_1px_0_rgb(255_255_255_/_0.025)]",
    className,
  );

  if (!onActivate) {
    return (
      <div
        {...(triggerProps as React.HTMLAttributes<HTMLDivElement>)}
        ref={forwardedRef as React.Ref<HTMLDivElement>}
        className={sharedClassName}
        aria-label={`${label}, ${isLoading ? "loading" : amount}`}
        aria-busy={isLoading}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      {...triggerProps}
      ref={forwardedRef as React.Ref<HTMLButtonElement>}
      type="button"
      className={cx(
        sharedClassName,
        "transition-colors hover:border-zinc-600 hover:bg-zinc-900/80",
        focusRing,
      )}
      aria-label={`${label}, ${isLoading ? "loading" : amount}`}
      aria-busy={isLoading}
      disabled={isLoading}
      onClick={onActivate}
    >
      {content}
    </button>
  );
});

CurrentBalance.displayName = "CurrentBalance";

export default CurrentBalance;
